/**
 * The terms of use, as players read and agree to them. Approved by the owner 2026-10-05 and in
 * force as `TERMS_VERSION` in the protocol: every account is asked to agree before
 * it does anything, and again whenever the version changes. Change the words here and the version there together.
 *
 * The owner's line on age: 18 and over, as a term the player agrees to. Nobody's age is checked, and the terms
 * say so: the player is the one answerable for the statement being true.
 */

export interface TermsSection {
  readonly heading: string;
  readonly items: readonly string[];
}

export const TERMS_INTRO =
  "By pressing “I agree”, making an account or playing, you agree to these terms. If you don't agree, don't use The Table.";

export const TERMS: readonly TermsSection[] = [
  {
    heading: "1. You are 18 or older",
    items: [
      "The Table is for adults. By agreeing you state that you are 18 or older.",
      "We don't check anyone's age and can't. We rely on what you tell us, and you are responsible for it being true.",
      "If you are under 18, don't make an account and don't play. An account found to belong to someone under 18 is closed.",
      "One person, one account. Don't share yours or make accounts for other people. You are responsible for what is done with it.",
    ],
  },
  {
    heading: "2. What it is",
    items: [
      "A tabletop roleplaying game whose DM and storyteller are AI models made by other companies (Anthropic and OpenAI). Whenever the DM or the story answers you, you are talking to an AI, not a person.",
      "What the AI writes is fiction, made up as you play. It can be wrong, odd or inconsistent, and it is not advice of any kind.",
      "You play by invitation: there is no public lobby, and you only share a table with people who were asked to it.",
      "It is being tested and changes often. Features, saved games and characters may change or be lost.",
    ],
  },
  {
    heading: "3. What you must not do",
    items: [
      "Don't try to get sexual content, hateful content, anything that sexualises someone under 18, or instructions for real-world harm to yourself or others. The game refuses these at every table.",
      "Don't use the game for anything but playing it: don't try to make the AI do other work, reveal its instructions, or get round its limits.",
      "Don't try to get round limits on how much or how fast you can play, including by making extra accounts.",
      "Don't attack, probe or overload the service, or try to reach other players' accounts, characters or games.",
      "Don't harass the people you play with.",
      "Don't upload pictures you have no right to use, or pictures of real people without their permission.",
      "Don't use the game to break the law. The AI providers' own usage policies apply to what you do here too.",
    ],
  },
  {
    heading: "4. What is checked and kept",
    items: [
      "What you type is checked automatically before the AI sees it, and lines that break section 3 are refused.",
      "When a line of yours is refused, or you are held back for playing faster than a person would, that is recorded against your account with what you typed, for an administrator to review. Only administrators see it.",
      "Enough refusals in a day stops your account until the next day. Misuse can end it.",
      "We keep your account name, a scrambled form of your password, your characters, your games and their stories, pictures you had drawn or uploaded, and problem reports you send.",
      "What you type and the story around it are sent to the AI providers to produce the game, and they handle it under their own terms.",
      "We don't sell your information or show you advertising. To have your account and what is kept under it deleted, ask the administrator who invited you.",
    ],
  },
  {
    heading: "5. Your content and ours",
    items: [
      "You keep whatever rights you have in what you write and the pictures you upload. You let us store it, send it to the AI providers, and use it to run, fix and improve the game.",
      "Some tables use rulebooks their host owns. That content is for play at that table only: don't copy it out.",
    ],
  },
  {
    heading: "6. Limits, and ending",
    items: [
      "An account may have a monthly allowance of play. When it is used up, the games that account hosts wait until the next month begins, or until the allowance is raised or set back. Allowances can change at any time.",
      "You can stop at any time. We may suspend or close an account that breaks these terms or puts the service or other players at risk, without warning, and we may shut the service down or reset its data at any time.",
    ],
  },
  {
    heading: "7. No guarantees",
    items: [
      "The Table is a hobby project, provided as it is. We don't promise it will be available, free of faults, or that your games will be kept.",
      "To the extent the law allows, we are not liable for loss arising from your use of it.",
      "When these terms change you will be asked to agree to the new version before you play again.",
    ],
  },
];
